<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Globe, Pencil } from 'lucide-vue-next'
import SupplierLogo from '../../SupplierLogo.vue'

export type ProfileTab = 'overview' | 'products' | 'training' | 'documents' | 'contacts'

const props = defineProps<{
  name: string
  logoUrl?: string | null
  websiteUrl?: string
  counts: { products: number; training: number; documents: number; contacts: number }
  canEdit: boolean
}>()
const tab = defineModel<ProfileTab>('tab', { required: true })

const tabs = computed(() => [
  { id: 'overview' as const, label: 'Overview' },
  { id: 'products' as const, label: 'Products', count: props.counts.products },
  { id: 'training' as const, label: 'Training', count: props.counts.training },
  { id: 'documents' as const, label: 'Documents', count: props.counts.documents },
  { id: 'contacts' as const, label: 'Contacts', count: props.counts.contacts },
])

const websiteLabel = computed(() => (props.websiteUrl || '').replace(/^https?:\/\//, '').replace(/\/$/, ''))

// Stuck = the sentinel above the bar has scrolled past the bar's top edge. Measuring
// the two elements avoids depending on which ancestor AppLayout scrolls.
const sentinel = ref<HTMLElement | null>(null)
const bar = ref<HTMLElement | null>(null)
const stuck = ref(false)
const measure = () => {
  if (sentinel.value && bar.value)
    stuck.value = sentinel.value.getBoundingClientRect().bottom < bar.value.getBoundingClientRect().top - 0.5
}
// Scroll events don't bubble; capture catches them from whichever element scrolls.
onMounted(() => { document.addEventListener('scroll', measure, { capture: true, passive: true }); measure() })
onBeforeUnmount(() => document.removeEventListener('scroll', measure, { capture: true }))

// Switching tabs while stuck: bring the panel's heading to just below the bar
// (#csp-tabpanel has scroll-margin-top = bar height + gap). Only the nearest scrolling
// ancestor moves; scrollIntoView would also shift AppLayout's overflow-hidden wrappers.
watch(tab, async () => {
  if (!stuck.value) return
  await nextTick()
  const panel = document.getElementById('csp-tabpanel')
  let scroller = panel?.parentElement ?? null
  while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement
  if (!panel || !scroller) return
  const margin = parseFloat(getComputedStyle(panel).scrollMarginTop) || 0
  scroller.scrollBy({ top: panel.getBoundingClientRect().top - scroller.getBoundingClientRect().top - margin })
})

const tabEls = ref<HTMLButtonElement[]>([])
function onKey(e: KeyboardEvent, i: number) {
  const n = tabs.value.length
  const next = { ArrowRight: (i + 1) % n, ArrowLeft: (i - 1 + n) % n, Home: 0, End: n - 1 }[e.key]
  if (next === undefined) return
  e.preventDefault()
  tab.value = tabs.value[next]!.id
  tabEls.value[next]?.focus()
}
</script>

<template>
  <header class="csp-surface">
    <div class="max-w-[1080px] mx-auto px-6 pt-5 pb-5">
      <div class="flex flex-col min-[860px]:flex-row gap-5 min-[860px]:items-start">
        <div class="flex gap-4 min-w-0 flex-1">
          <SupplierLogo :src="logoUrl" :website="websiteUrl" :alt="name"
            class="w-[72px] h-[72px] rounded-[6px] shrink-0 object-contain border border-(--csp-border)" />
          <div class="min-w-0">
            <h1 class="text-2xl font-bold text-(--csp-text) leading-tight">{{ name }}</h1>
            <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[13px] csp-muted">
              <a v-if="websiteUrl" :href="websiteUrl" target="_blank" rel="noopener" class="csp-link inline-flex items-center gap-1">
                <Globe :size="13" aria-hidden="true" /> {{ websiteLabel }}
              </a>
              <span>{{ counts.products }} products</span>
              <span>{{ counts.contacts }} channel contacts</span>
            </div>
          </div>
        </div>

        <div class="flex gap-2 shrink-0">
          <router-link v-if="canEdit" :to="{ name: 'supplier-settings' }" class="csp-btn csp-btn-primary">
            <Pencil :size="15" aria-hidden="true" /> Edit profile
          </router-link>
        </div>
      </div>
    </div>
  </header>

  <!-- Zero-height marker right above the tab bar: once it scrolls above the bar, the bar is stuck. -->
  <div ref="sentinel" aria-hidden="true"></div>

  <!-- Sibling of <header> (not inside it) so it can stay stuck for the rest of the page. -->
  <div ref="bar" class="csp-tabbar">
    <div class="max-w-[1080px] mx-auto px-6 h-full flex items-end">
      <!-- Always rendered; width + opacity animate so the tabs slide instead of jumping. -->
      <div class="csp-mini hidden min-[860px]:flex items-center gap-2 shrink-0 self-center overflow-hidden whitespace-nowrap" :class="{ 'is-stuck': stuck }" aria-hidden="true">
        <SupplierLogo :src="logoUrl" :website="websiteUrl" :alt="name"
          class="w-6 h-6 rounded-[4px] shrink-0 object-contain border border-(--csp-border)" />
        <span class="text-sm font-semibold text-(--csp-text) truncate">{{ name }}</span>
      </div>

      <div role="tablist" aria-label="Company profile sections" class="csp-tablist flex gap-6 min-w-0 flex-1 overflow-x-auto">
        <button v-for="(t, i) in tabs" :key="t.id" :id="`csp-tab-${t.id}`" ref="tabEls" type="button" role="tab"
          :aria-selected="tab === t.id" aria-controls="csp-tabpanel" :tabindex="tab === t.id ? 0 : -1"
          class="csp-tab pb-3 text-sm whitespace-nowrap border-b-2"
          :class="tab === t.id ? 'font-semibold text-(--csp-text) border-(--csp-brand)' : 'text-(--csp-text-2) border-transparent hover:text-(--csp-text)'"
          @click="tab = t.id" @keydown="onKey($event, i)">
          {{ t.label }}<span v-if="t.count !== undefined" class="font-normal csp-muted ml-1.5">{{ t.count }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
